import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, PartyPopper, Loader2 } from 'lucide-react';
import { useLanguage } from './TranslationProvider';
import { registerLead } from './crm';
// Mensaje de bienvenida traducido (se conserva el mismo significado en cada idioma)
const WELCOME: Record<string, {
  title: string;
  body: string;
}> = {
  es: {
    title: '¡Bienvenido a esta comunidad!',
    body: 'Gracias por unirte al Concilio Vecinal de Sylmar. Te mantendremos informado sobre reuniones, recursos y noticias importantes para tu vecindario.'
  },
  en: {
    title: 'Welcome to this community!',
    body: 'Thank you for joining the Sylmar Neighborhood Council. We will keep you informed about meetings, resources, and important news for your neighborhood.'
  },
  fr: {
    title: 'Bienvenue dans cette communauté !',
    body: 'Merci de rejoindre le Sylmar Neighborhood Council. Nous vous tiendrons informé des réunions, des ressources et des actualités importantes pour votre quartier.'
  },
  'zh-CN': {
    title: '欢迎加入本社区！',
    body: '感谢您加入 Sylmar 社区委员会。我们将及时向您通报会议、资源以及对您所在社区重要的新闻。'
  },
  'zh-TW': {
    title: '歡迎加入本社區！',
    body: '感謝您加入 Sylmar 社區委員會。我們將及時向您通報會議、資源以及對您所在社區重要的新聞。'
  },
  ko: {
    title: '이 커뮤니티에 오신 것을 환영합니다!',
    body: 'Sylmar 주민 위원회에 가입해 주셔서 감사합니다. 회의, 자원 및 지역사회에 중요한 소식을 계속 알려드리겠습니다.'
  },
  hy: {
    title: 'Բարի գալուստ այս համայնք:',
    body: 'Շնորհակալություն Sylmar-ի հարևանության խորհրդին միանալու համար: Մենք ձեզ կտեղեկացնենք ժողովների, ռեսուրսների և ձեր թաղամասի համար կարևոր նորությունների մասին:'
  },
  tl: {
    title: 'Maligayang pagdating sa komunidad na ito!',
    body: 'Salamat sa pagsali sa Sylmar Neighborhood Council. Papanatilihin ka naming may alam tungkol sa mga pulong, mapagkukunan, at mahahalagang balita para sa iyong kapitbahayan.'
  }
};
export function LoginModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    lang
  } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'form' | 'saving' | 'done'>('form');
  const [error, setError] = useState('');
  // Cerrar con Escape y bloquear el scroll del fondo mientras está abierto
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  // Reiniciar el formulario al reabrir
  useEffect(() => {
    if (open) {
      setStatus('form');
      setError('');
    }
  }, [open]);
  if (!open) return null;
  const welcome = WELCOME[lang] || WELCOME.en;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim()) {
      setError('Por favor completa tu nombre y correo. / Please enter your name and email.');
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) {
      setError('Ingresa un correo válido. / Please enter a valid email.');
      return;
    }
    setStatus('saving');
    await registerLead({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      lang,
      source: 'login_modal'
    });
    setStatus('done');
  };
  return <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="login-modal-title" data-id="element-112" data-mp="2fuqj9pi6roqg">
      {/* Fondo */}
      <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-113" data-mp="264x7ane4t7lo"></div>

      {/* Tarjeta */}
      <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden" data-id="element-114" data-mp="1x2xpvtz7g1i7">
        <div className="bg-sylmar p-5 flex items-center justify-between text-white" data-id="element-115" data-mp="1p286vnqmhfnw">
          <div className="flex items-center gap-3" data-id="element-116" data-mp="1k3etrwf8zqo9">
            <div className="bg-white/15 p-2 rounded-xl" data-id="element-117" data-mp="1k4q1267wjgk4">
              <User className="w-5 h-5" data-id="element-118" data-mp="1fcg9klr528fq" />
            </div>
            <div data-id="element-119" data-mp="1favemv0wwnhc">
              <h3 id="login-modal-title" className="font-black text-lg leading-tight" data-id="element-120" data-mp="1fac1rsq4cpa6">
                Únete a la Comunidad
              </h3>
              <p className="text-sylmar-light text-xs italic" data-id="element-121" data-mp="169xe4mpomy3i">
                Join the Community
              </p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-1.5 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-122" data-mp="wd2h689255rc">
            <X className="w-5 h-5" data-id="element-123" data-mp="2mqji3nc7ml48" />
          </button>
        </div>

        {status === 'done' ?
      // Estado de bienvenida
      <div className="p-8 text-center" data-id="element-124" data-mp="8hplypqnx3yf">
            <div className="inline-flex p-4 bg-sylmar-light rounded-full mb-4" data-id="element-125" data-mp="dhlqqr76ozf1">
              <PartyPopper className="w-9 h-9 text-sylmar" data-id="element-126" data-mp="dfhad9s3m7xv" />
            </div>
            <h4 className="text-2xl font-black text-charcoal mb-2" data-id="element-127" data-mp="i8u0xpgc8ot9">
              {welcome.title}
            </h4>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-128" data-mp="m94835fv9l9p">{welcome.body}</p>
            <button onClick={onClose} className="w-full bg-sylmar hover:bg-sylmar-dark text-white font-bold py-3 rounded-xl transition-colors" data-id="element-129" data-mp="3lqllwjw248hy">
              Continuar / Continue
            </button>
          </div> :
      // Formulario de registro
      <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-130" data-mp="3kz54071a4by">
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-131" data-mp="1a09iv66y9nd5">
              Regístrate para recibir noticias, recordatorios de reuniones y
              recursos de tu comunidad.
              <span className="block text-xs text-gray-400 italic mt-1" data-id="element-132" data-mp="3b16cq9sbz85m">
                Register to receive news, meeting reminders, and community
                resources.
              </span>
            </p>

            <div data-id="element-133" data-mp="3l05dl66utzi2">
              <label htmlFor="lead-name" className="block text-sm font-bold text-charcoal mb-1" data-id="element-134" data-mp="3kykinfgmoejo">
                Nombre / Name
              </label>
              <div className="relative" data-id="element-135" data-mp="2sbzqwdw8q0fl">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-136" data-mp="2s8vcyosz3qbq" />
                <input id="lead-name" type="text" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-sylmar focus:outline-none transition-colors" placeholder="María García" data-id="element-137" data-mp="2naaeouxa7gpx" />
              </div>
            </div>

            <div data-id="element-138" data-mp="k1i6b7q5xd7m">
              <label htmlFor="lead-email" className="block text-sm font-bold text-charcoal mb-1" data-id="element-139" data-mp="oubb1vlmsljp">
                Correo / Email
              </label>
              <div className="relative" data-id="element-140" data-mp="f4hyosdo02ee">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-141" data-mp="f5jltv7ygfch" />
                <input id="lead-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-sylmar focus:outline-none transition-colors notranslate" placeholder="maria@email.com" data-id="element-142" data-mp="12v5lejs8owl0" />
              </div>
            </div>

            <div data-id="element-143" data-mp="3jjhqibmqbv1i">
              <label htmlFor="lead-phone" className="block text-sm font-bold text-charcoal mb-1" data-id="element-144" data-mp="3jk1b603rkxj8">
                Teléfono (opcional) / Phone (optional)
              </label>
              <div className="relative" data-id="element-145" data-mp="a46jte0e8u1o">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-146" data-mp="53se42m9lw6t" />
                <input id="lead-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-sylmar focus:outline-none transition-colors notranslate" placeholder="(818) 555-0100" data-id="element-147" data-mp="4o6hgezo6il" />
              </div>
            </div>

            {error && <p className="text-sm text-red-600 font-medium" data-id="element-148" data-mp="1gixrc0k0vd4o">{error}</p>}

            <button type="submit" disabled={status === 'saving'} className="w-full flex items-center justify-center gap-2 bg-sylmar hover:bg-sylmar-dark text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-70" data-id="element-149" data-mp="1bqw7ntkiyk4m">
              {status === 'saving' ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-150" data-mp="1t2l2wv7g7rju" /> Registrando...
                </> : <>Unirme / Join</>}
            </button>
            <p className="text-[11px] text-gray-400 text-center leading-relaxed" data-id="element-151" data-mp="220wogitgjz5f">
              Tus datos se usan solo para comunicarnos contigo sobre la
              comunidad. / Your information is only used to contact you about
              the community.
            </p>
          </form>}
      </div>
    </div>;
}